import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import format from 'date-fns/format';
import enGB from 'date-fns/locale/en-GB';
import { addMonths, endOfDay, min, startOfDay, subDays, subMonths } from 'date-fns';
import { DataGrid, FilterAutoCompleteById, FilterAsyncAutoComplete } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { createAsyncReport, getAsyncReports, getAsyncReportDownloadUrl } from 'src/api/admin/reports';
import { Alert, Button } from '@mui/material';
import { ReportStatusChip } from '@repo/components';
import { ReportStatus, Report } from '@repo/shared/domain';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
import { getSites } from 'src/api/admin/sites';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/reports/time-on-site')({
	component: TimeOnSitePage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: search.page as number | undefined,
		};
	},
});

const columns = [
	{
		field: 'name',
		headerName: 'File name',
		sortable: false,
		flex: 3,
	},
	{
		field: 'createdAt',
		headerName: 'Generated At',
		sortable: false,
		flex: 1,
		renderCell: (params: { value: string }) => format(new Date(params.value), 'dd/MM/yyyy HH:mm'),
	},
	{
		field: 'status',
		headerName: 'Status',
		sortable: false,
		flex: 1,
		renderCell: (params: { value: ReportStatus }) => <ReportStatusChip status={params.value} />,
	},
];
interface SearchParams {
	page?: number;
}
interface GenerateParams {
	startDate: Date | null;
	endDate: Date | null;
	roleIds?: string[];
	groupIds?: string[];
	siteId?: string;
}

function TimeOnSitePage() {
	const { user: authenticatedUser } = useAuthenticatedUser();
	const search = Route.useSearch();
	const navigate = useNavigate();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();
	const [generateParams, setGenerateParams] = useState<GenerateParams>({
		startDate: (() => {
			const now = new Date();
			const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
			return firstDayPrevMonth;
		})(),
		endDate: (() => {
			const now = new Date();
			const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
			return lastDayPrevMonth;
		})(),
		roleIds: [],
		groupIds: [],
	});
	const yesterday = useMemo(() => subDays(startOfDay(new Date()), 1), []);

	const maxEndDate =
		generateParams.startDate != null ? min([yesterday, addMonths(startOfDay(generateParams.startDate), 3)]) : yesterday;
	const minStartDate = generateParams.endDate != null ? subMonths(startOfDay(generateParams.endDate), 3) : undefined;
	const maxStartDate = generateParams.endDate != null ? min([generateParams.endDate, yesterday]) : yesterday;

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasPermission = verifyPermission([PERMISSIONS.adminReportsView]);

	useBreadcrumbLevel(0, 'Reports');
	useBreadcrumbLevel(1, 'Time on Site');

	// Fetch roles for autocomplete
	const { data: rolesData } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: getRolesForAutocomplete,
		enabled: hasPermission,
	});

	// Fetch sites
	const { data: sitesData } = useQuery({
		queryKey: ['sites-list'],
		queryFn: () => getSites('noPagination=true'),
		enabled: hasPermission,
	});

	const sites = useMemo(() => sitesData?.data || [], [sitesData?.data]);
	const hasSingleSite = sites.length === 1;
	const roles = rolesData || [];

	// Fetch time-on-site report
	const { data, isLoading, refetch } = useQuery({
		queryKey: ['reports-time-on-site', search.page || 0],
		queryFn: () =>
			getAsyncReports('time-on-site', {
				page: search.page || 0,
			}),
		enabled: hasPermission,
	});

	// Redirect if no permissions
	useEffect(() => {
		if (!hasPermission) {
			navigate({
				to: '/403',
				search: {
					permissions: 'adminReportsView',
					occurredAt: '/reports/time-on-site',
					method: 'GET',
					uuid: undefined,
				},
			});
		}
	}, [hasPermission, navigate]);

	useEffect(() => {
		if (hasSingleSite && sites[0] && !generateParams.siteId) {
			setGenerateParams((prev) => ({ ...prev, siteId: sites[0]!.id }));
		}
	}, [hasSingleSite, sites, generateParams.siteId]);

	if (!hasPermission) {
		return null;
	}

	const handleDateFromChange = (date: Date | null) => {
		if (!date || isNaN(date.getTime())) {
			setGenerateParams({ ...generateParams, startDate: null });
			return;
		}
		const updates: Partial<GenerateParams> = { startDate: date };
		if (
			generateParams.endDate &&
			(date > generateParams.endDate || generateParams.endDate > addMonths(startOfDay(date), 3))
		) {
			updates.endDate = null;
		}
		setGenerateParams({ ...generateParams, ...updates });
	};
	const handleDateToChange = (date: Date | null) => {
		if (!date || isNaN(date.getTime())) {
			setGenerateParams({ ...generateParams, endDate: null });
			return;
		}
		setGenerateParams({ ...generateParams, endDate: date });
	};

	const handleGenerateParamsChange = (params: Partial<GenerateParams>) => {
		setGenerateParams({ ...generateParams, ...params });
	};

	const generateReport = async () => {
		if (!generateParams.startDate || !generateParams.endDate || !generateParams.siteId) {
			return;
		}
		try {
			await createAsyncReport('time-on-site', {
				startDate: startOfDay(generateParams.startDate),
				endDate: endOfDay(generateParams.endDate),
				siteId: generateParams.siteId,
				roleIds: generateParams.roleIds,
				groupIds: generateParams.groupIds,
			});
		} catch (error) {
			console.error(error);
		} finally {
			refetch();
		}
	};

	const handleRowClick = async ({ row }: { id: string; row: Report }) => {
		if (row.status === 'completed') {
			const url = await getAsyncReportDownloadUrl('time-on-site', row.id);
			window.open(url, '_blank');
		}
	};
	const revalidateTimeOnSite = () => {
		queryClient.invalidateQueries({ queryKey: ['reports-time-on-site'] });
		refetch();
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const isDateRangeTooLong =
		!!generateParams.startDate &&
		!!generateParams.endDate &&
		generateParams.endDate > addMonths(startOfDay(generateParams.startDate), 3);
	const isInvalidDateRange =
		!!generateParams.startDate &&
		!!generateParams.endDate &&
		(generateParams.startDate > generateParams.endDate || generateParams.endDate > yesterday || isDateRangeTooLong);
	const generationDisabled =
		!generateParams.siteId || !generateParams.startDate || !generateParams.endDate || isInvalidDateRange;

	return (
		<>
			<div className="px-4 pt-4 flex flex-col flex-wrap gap-2 w-full justify-between items-center pb-4">
				{data?.data?.some((report: Report) => report.status === 'pending') && (
					<Alert
						severity="info"
						className="w-full"
						action={
							<Button color="inherit" size="small" onClick={revalidateTimeOnSite}>
								Refresh
							</Button>
						}
					>
						A report is being generated. This may take a few minutes.
					</Alert>
				)}
				<div
					className="flex flex-col gap-3 w-full"
					style={{
						border: '1px solid #e0e0e0',
						borderRadius: 8,
						padding: 16,
						backgroundColor: '#fcfcfc',
					}}
				>
					<Alert variant="outlined" severity="info">
						This report shows how much time users spent on site (in hh:mm:ss) during the date range. The date range must
						not exceed three months.
					</Alert>
					<div className="flex flex-row flex-wrap gap-2 items-start">
						<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
							<DatePicker
								value={generateParams.startDate}
								onChange={handleDateFromChange}
								minDate={minStartDate}
								maxDate={maxStartDate}
								slotProps={{
									textField: {
										size: 'small',
										sx: { flex: '1 1 150px', backgroundColor: '#fff' },
									},
								}}
							/>
							<DatePicker
								value={generateParams.endDate}
								onChange={handleDateToChange}
								minDate={generateParams.startDate || undefined}
								maxDate={maxEndDate}
								slotProps={{
									textField: {
										size: 'small',
										sx: { flex: '1 1 150px', backgroundColor: '#fff' },
									},
								}}
							/>
						</LocalizationProvider>
						{!hasSingleSite && (
							<FilterAutoCompleteById
								key="siteFilter"
								label="Filter Sites*"
								required={true}
								options={sites}
								value={generateParams.siteId || ''}
								onChange={(siteId) => handleGenerateParamsChange({ siteId: siteId as string })}
								size="small"
								sx={{ flex: '1 1 200px', maxWidth: 300 }}
							/>
						)}
					</div>
					<div className="flex flex-row flex-wrap gap-2 items-center">
						<FilterAutoCompleteById
							key="roleFilter"
							label="Filter Roles"
							options={roles}
							value={generateParams.roleIds || []}
							onChange={(roleIds) =>
								handleGenerateParamsChange({ roleIds: roleIds ? (roleIds as string[]) : undefined })
							}
							size="small"
							sx={{ flex: '1 1 200px' }}
							multiple
						/>
						<FilterAsyncAutoComplete
							key={generateParams.groupIds?.length ? 'groupFilter' : 'groupFilterNoValue'}
							name="groupIds"
							label="Filter Companies"
							optionLabel="name"
							dataSource="/api/data-sources/groups/data"
							value={generateParams.groupIds || []}
							onChange={(newGroupIds) => handleGenerateParamsChange({ groupIds: newGroupIds })}
							multiple
							size="small"
							sx={{ flex: '1 1 200px' }}
						/>
					</div>
					<div className="flex justify-end">
						<Button
							variant="contained"
							color="primary"
							disabled={generationDisabled}
							onClick={generateReport}
							sx={{ width: 250 }}
						>
							Generate Report
						</Button>
					</div>
				</div>
			</div>
			<div className="p-4 pt-4">
				<DataGrid
					data={data?.data || []}
					columns={columns}
					rowCount={data?.count || 0}
					pageSize={data?.pageSize || 10}
					page={data?.page || 0}
					onPaginationModelChange={handlePaginationModelChange}
					isLoading={isLoading}
					paginationMode="server"
					sortingMode="server"
					onRowClick={handleRowClick}
					sortingOrder={['asc', 'desc']}
					pagination
					onReload={revalidateTimeOnSite}
					getRowClassName={() => '!cursor-default'}
				/>
			</div>
		</>
	);
}
